import type { Ref } from 'vue'
import { apiErrorMessage, apiValidationErrors } from '@/lib/errors'

export interface RequestGuard {
  /** Call when issuing a new request; returns a token identifying it as the latest. */
  start(): number
  /** True if `token` is still the most recently started request. */
  isCurrent(token: number): boolean
  /** The latest token, without starting a new request. */
  current(): number
}

// Tracks which of a series of requests (e.g. one per keystroke, or one per
// pagination click) was issued last, so a response for an older request can
// be told apart from the response everyone actually wants to see.
export function createRequestGuard(): RequestGuard {
  let latest = 0
  return {
    start: () => ++latest,
    isCurrent: (token) => token === latest,
    current: () => latest,
  }
}

// Shared shape for composable fetch/mutate actions: clear `error`, toggle
// `loading` (if given) around the call, and translate a thrown error into a
// friendly message on `error`. Pass `fieldErrors` for a form submit: it is
// cleared on every attempt and repopulated from a 422 body. Returns the
// action's result, or `undefined` if it failed.
//
// Pass `isStale` (backed by a `RequestGuard`) when `action` is one of several
// in-flight requests that write to shared state — e.g. a fetch re-triggered
// by every filter keystroke or pagination click. When the call resolves
// after a newer one has already started, `isStale()` returns true and the
// error/loading side effects are skipped so a late response can't clobber
// state a newer, already-applied response set. The caller's `action` is
// still responsible for checking `isStale`/the guard itself before writing
// its own result into `data`/`meta`.
export async function withAsyncState<T>(
  error: Ref<string | null>,
  fallbackMessage: string,
  action: () => Promise<T>,
  loading?: Ref<boolean>,
  fieldErrors?: Ref<Record<string, string[]>>,
  isStale?: () => boolean,
): Promise<T | undefined> {
  if (loading) loading.value = true
  error.value = null
  if (fieldErrors) fieldErrors.value = {}
  try {
    const result = await action()
    return isStale?.() ? undefined : result
  } catch (e: unknown) {
    if (isStale?.()) return undefined
    error.value = apiErrorMessage(e, fallbackMessage)
    if (fieldErrors) fieldErrors.value = apiValidationErrors(e)
    return undefined
  } finally {
    if (loading && !isStale?.()) loading.value = false
  }
}
